import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  ReactNode
} from 'react';

/**
 * Context for managing YouTube player state and operations
 * Bridges the player instance from VideoContainer to VideoChaptersSidebar
 */
interface VideoPlayerContextValue {
  // Player state
  currentVideoTime: number;
  isPlayerReady: boolean;

  // Player operations
  seekTo: (seconds: number) => void;
  seekRelative: (offsetSeconds: number) => void;
  playVideo: () => void;
  pauseVideo: () => void;

  // Registration methods (called by VideoContainer)
  registerPlayer: (player: any) => void;
  setCurrentTime: (time: number) => void;
  setPlayerReady: (ready: boolean) => void;
}

const VideoPlayerContext = createContext<VideoPlayerContextValue | undefined>(
  undefined
);

interface VideoPlayerProviderProps {
  children: ReactNode;
}

/**
 * Provider for video player state
 * Should wrap both VideoContainer and VideoChaptersSidebar components
 */
export const VideoPlayerProvider: React.FC<VideoPlayerProviderProps> = ({
  children
}) => {
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const playerRef = useRef<any>(null);

  const registerPlayer = useCallback((player: any) => {
    console.log('[VideoPlayerContext] Registering player');
    playerRef.current = player;
    setIsPlayerReady(true);
  }, []);

  const setCurrentTime = useCallback((time: number) => {
    setCurrentVideoTime(time);
  }, []);

  const setPlayerReady = useCallback((ready: boolean) => {
    setIsPlayerReady(ready);
  }, []);

  const seekTo = useCallback(
    (seconds: number) => {
      if (!playerRef.current || !isPlayerReady) {
        console.warn(
          '[VideoPlayerContext] Player not ready, cannot seek to',
          seconds
        );
        return;
      }

      // Seek to timestamp minus 2 seconds (minimum 0)
      const seekTime = Math.max(0, seconds - 2);

      console.log('[VideoPlayerContext] Seeking to timestamp:', {
        requested: seconds,
        adjusted: seekTime
      });

      playerRef.current.seekTo(seekTime, true);
      playerRef.current.playVideo();
    },
    [isPlayerReady]
  );

  const seekRelative = useCallback(
    (offsetSeconds: number) => {
      if (!playerRef.current || !isPlayerReady) {
        console.warn(
          '[VideoPlayerContext] Player not ready, cannot seek relative',
          offsetSeconds
        );
        return;
      }

      try {
        const currentTime = playerRef.current.getCurrentTime();
        const newTime = Math.max(0, currentTime + offsetSeconds);

        console.log('[VideoPlayerContext] Seeking relative:', {
          currentTime,
          offset: offsetSeconds,
          newTime
        });

        playerRef.current.seekTo(newTime, true);
        setCurrentVideoTime(newTime);
      } catch (error) {
        console.error('[VideoPlayerContext] Error seeking relative:', error);
      }
    },
    [isPlayerReady]
  );

  const playVideo = useCallback(() => {
    if (!playerRef.current || !isPlayerReady) {
      console.warn('[VideoPlayerContext] Player not ready, cannot play');
      return;
    }
    playerRef.current.playVideo();
  }, [isPlayerReady]);

  const pauseVideo = useCallback(() => {
    if (!playerRef.current || !isPlayerReady) {
      console.warn('[VideoPlayerContext] Player not ready, cannot pause');
      return;
    }
    playerRef.current.pauseVideo();
  }, [isPlayerReady]);

  const value: VideoPlayerContextValue = {
    currentVideoTime,
    isPlayerReady,
    seekTo,
    seekRelative,
    playVideo,
    pauseVideo,
    registerPlayer,
    setCurrentTime,
    setPlayerReady
  };

  return (
    <VideoPlayerContext.Provider value={value}>
      {children}
    </VideoPlayerContext.Provider>
  );
};

/**
 * Hook to access video player context
 * Must be used within VideoPlayerProvider
 */
export const useVideoPlayer = (): VideoPlayerContextValue => {
  const context = useContext(VideoPlayerContext);
  if (!context) {
    throw new Error('useVideoPlayer must be used within VideoPlayerProvider');
  }
  return context;
};

/**
 * Optional variant — returns null when not inside VideoPlayerProvider.
 * Safe to use in components that may be rendered outside a provider.
 */
export const useVideoPlayerOptional = (): VideoPlayerContextValue | null => {
  return useContext(VideoPlayerContext) ?? null;
};
